<template>
  <div
    v-if="!hide"
    class="input-field"
    :style="[{
      width: inputFieldSize + '%',
      alignItems: alignLabel
    }, inputFieldContainerStyling]"
  >
    <label
      v-if="label"
      class="form-label-div"
      :class="{ disabled: disabled }"
      style="overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 90%;"
    >
      {{ label }}<span v-if="required">*</span><span v-if="badLabel" class="errorLabel">Limit is: {{limit}} </span>
    </label>

    <div class="input-field-container">
      <div class="input-container" :style="{ justifyContent: inputContainerAlign }">
        <input
          v-if="inputType === 'text'"
          type="text"
          :value="value"
          :disabled="disabled || (labelIsLocked && variableName.includes('_label'))"
          :placeholder="placeholder"
          :class="{
            'with-label-button': automaticLabel,
            'invalid': !isValid
          }"
          :id="fieldId"
          :style="inputStyle"
          @input="updateValue($event.target.value)"
          @keypress="
            restrictSpecialCharacters
              ? nameKeydown($event)
              : checkAlphanumeric($event)"
        />

        <input
          v-else-if="inputType === 'password'"
          type="password"
          :value="value"
          :disabled="disabled"
          :style="inputStyle"
          :class="{
            'invalid': !isValid
          }"
          autocomplete="new_password"
          :minlength="8"
          @input="updateValue($event.target.value)"
        />

        <textarea
          v-else-if="inputType === 'textarea'"
          type="textarea"
          :class="{
            'invalid': !isValid
          }"
          :value="value"
          :disabled="disabled"
          :style="inputStyle"
          @input="updateValue($event.target.value)"
        />

        <input
          v-else-if="inputType === 'email'"
          type="email"
          :value="value"
          :disabled="disabled"
          :placeholder="placeholder"
          :id="fieldId"
          :style="inputStyle"
          @input="updateValue($event.target.value)"
        />

        <div
          v-else-if="inputType === 'checkbox'"
          v-for="checkbox in checkboxOptions"
          :key="checkbox.id"
          :style="{ 'padding': '0 0 0 1px' }"
        >
          <custom-checkbox
            :value="value"
            :label="checkbox.label"
            :disabled="disabled"
            :height="height"
            :width="width"
            :border="border"
            :checkbox-style="inputStyle"
            :checkboxLabelStyle="checkboxLabelStyle"
            :checkbox-icon-style="checkboxIconStyle"
            :indeterminate="indeterminate"
            @update-value="updateValue(!value)"
          />
        </div>

        <input
          v-else-if="
            inputType === 'number' ||
            inputType === 'int'||
            inputType === 'float' ||
            inputType === 'int_pos' ||
            inputType === 'float_pos' ||
            inputType === 'number_pos'
          "
          type="number"
          :class="{
            'invalid': !isValid
          }"
          :value="value"
          :disabled="disabled"
          :placeholder="placeholder"
          :min="min"
          :max="max"
          :step="step ? step : autoNumberFieldStep"
          :id="fieldId"
          :style="inputStyle"
          @input="updateValue($event.target.value)"
          @keypress="checkInput"
        />

        <treeselect
          v-else-if="
            inputType === 'select' ||
            inputType === 'multi-select' ||
            inputType === 'multiselect'
          "
          class="form-input-select"
          disableFuzzyMatching
          appendToBody
          :value="value"
          :options="selectOptionsList ? selectOptionsList : options"
          :load-options="loadOptions"
          :normalizer="normalizer"
          :multiple="inputType === 'multi-select' || inputType === 'multiselect'"
          :placeholder="treeSelectPlaceHolder ? treeSelectPlaceHolder : selectPlaceholder"
          :disabled="disabled"
          :clearable="clearable"
          :style="inputStyle"
          :disableBranchNodes="disableBranchNodes"
          @input="updateValue($event)"
        />

        <treeselect
        	v-else-if="inputType === 'user'"
          disableFuzzyMatching
          class="form-input-select"
          :value="value"
          :options="options"
          :load-options="loadUserOptions"
          :normalizer="userNormalizer"
          :placeholder="placeholder"
          :disabled="disabled"
          :clearable="clearable"
          :style="inputStyle"
          @input="updateValue($event)"
        />

        <v-radio-group
          v-else-if="inputType === 'radio'"
          id="radio-buttons"
          class="radio-input"
          :value="value"
          :input-value="value"
          :column="radioInColumn"
          :hide-details="true"
          :style="inputStyle"
          @change="updateValue($event)"
        >
          <v-radio
            v-for="option in radioOptions"
            :color="inputFieldColor"
            :key="option.label"
            :label="option.label"
            :value="convertedValue(option.value)"
            :ripple="false"
            :disabled="disabled"
          />
        </v-radio-group>

        <div v-else-if="inputType === 'url'" class="url">
          <a
            v-if="disabled && value"
            :href="getValidURL(value)"
            target="_blank"
            class="accent--text"
            style="background-color: white"
          >
            {{ value }}
          </a>

          <input
            v-else
            :value="value"
            @input="updateValue($event.target.value)"
            type="text"
            :disabled="disabled"
            :placeholder="placeholder"
            :style="inputStyle"
          />
        </div>

        <div v-else-if="inputType === 'date'" class="date">
          <input
            type="date"
            :disabled="disabled"
            :value="getDate"
            :style="inputStyle"
            @input="updateValue($event.target.value)"
          />

          <v-btn
            v-if="!disabled && !required && value"
            @click="updateValue(null)"
            x-small
            text
            icon
            class="form-input-clear-button"
          >
            <v-icon size="15">
              clear
            </v-icon>
          </v-btn>
        </div>

        <div v-else-if="inputType === 'datetime'" class="datetime">
          <input
            type="datetime-local"
            style="width: 100%"
            :value="dateTimeValue"
            :disabled="disabled"
            :min="min"
            :max="max"
            :step="step"
            :style="inputStyle"
            @change="setDateTimeValue($event.target.value)"
          />

          <v-btn
            v-if="!disabled && !required && value"
            @click="updateValue(null)"
            x-small
            text
            icon
            class="form-input-clear-button"
          >
            <v-icon size="15">
              clear
            </v-icon>
          </v-btn>
        </div>

        <drop-file-area
          ref="dropFileInput"
          v-else-if="inputType === 'file'"
          :disabled="disabled"
          :file-types="fileTypes"
          :multiple-files="multipleFiles"
          @updateFile="updateValue($event)"
          @trashFile="updateValue(null)"
        />

        <div
          v-else-if="inputType === 'other'"
          class="input-container"
          :disabled="disabled"
        >
          <slot />
        </div>
      </div>

      <v-icon
        v-if="fieldIsLabel"
        @click="$emit('toggle-label-lock')"
        :disabled="userCanUnlockLabel"
        style="color: var(--v-accent-base)"
      >
        {{ labelIsLocked ? 'mdi-lock' : 'mdi-lock-open' }}
      </v-icon>

      <slot name="front-slot" />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import CustomCheckbox from "./CustomCheckbox";
import DropFileArea from '../../components/_Molecules/DropFileArea';
import { bus } from "@/main.js";
import utils from "@/utils";

export default {
  name: "InputField",

  components: {
    CustomCheckbox,
    DropFileArea,
  },

  props: {
    validationObject: {
      type: Object,
      required: false,
    },
    /**
     * Name of the input field
     */
    label: {
      type: String,
      required: false
    },
    /**
     * Type of input field.
     */
    inputType: {
      type: String,
      required: true
    },
    /**
     * Name of the variable to use.
     */
    value: {
      required: true
    },
    /**
     * Limit of the input
     */
    limit:{
      type: String
    },
    /**
     * Name of the variable
     */
    variableName: {
      type: String
    },
    /**
     * Size of the input field.
     */
    size: {
      type: String,
      default: function() {
        return this.inputType === 'select' || this.inputType === 'multi-select' || this.inputType === 'multiselect'
        ? 'large'
        : 'small'
      }
    },
    /**
     * Indicates if the input is bad in order to show the respective label warning
     */
    badLabel: {
      type: Boolean
    },
    /**
     * Defines if input field is required.
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Defines if input field is disabled.
     */
    inputDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Defines if tab is disabled.
     */
    tabDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Defines if form is disabled.
     */
    formDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Defines if the input field is to be hidden.
     */
    hide: {
      type: Boolean,
      default: false
    },
    /**
     * Name of options list to use in the Select field.
     */
    selectOptions: {
      type: String,
      required: false
    },
    /**
     * List of options to show in the Radio field.
     */
    radioOptions: {
      type: Array,
      required: false
    },
    /**
     * List of options to show in the Checkbox field.
     */
    checkboxOptions: {
      type: Array,
      required: false
    },
    /**
     * Indicates if the options of the radio button are displayed vertically
     */
    displayRadioOptionsInColumn: {
      type: Boolean,
      default: false,
    },
    /**
     * Indicates if the label is locked
     */
    labelIsLocked: {
      type:Boolean
    },
    /**
     * Indicates if the user can unlock the label
     */
    userCanUnlockLabel:{
      type: Boolean,
      default: false,
    },
    /**
     * Determines if the label can be created automatically
     */
    automaticLabel: {
      type: Boolean
    },
    //if used user cant type in '-' (when using min user cant use negative numbers using the input arrows but can put it in manually)
    onKeyDown: {
      type: String,
      default: null
    },
    /**
     * Increment in value in each step
     */
    step: {
      type: Number | String
    },
    /**
     * Minimum value of the input
     */
    min: {
      type: Number | String
    },
    /**
     * Maximum value of the input
     */
    max: {
      type: Number | String
    },
    /**
     * Input placeholder
     */
    placeholder: {
      type: String
    },
    /**
     * Indicates if the input can be cleared
     */
    clearable: {
      type: Boolean,
      default: true
    },
    /**
     * Indicates if the input must contain only alphanumeric characters
     */
    restrictSpecialCharacters: {
      type: Boolean,
      default: false
    },
    /**
     * Indicates where the label is aligned
     */
    alignLabel: {
      type: String,
      default: 'flex-start',
    },
    /**
     * Indicates the color of the input
     */
    inputFieldColor: {
      type: String,
      default: '#29BDF0',
    },
    /**
     * Indicates the height of the input
     */
    height: {
      type: Number,
    },
    /**
     * Indicates the width of the input
     */
    width: {
      type: Number,
    },

    border: {
      type: String,
    },
    /**
     * Optional styling to override the default styling of the input
     */
    inputStyle: {
      type: Object,
    },
    /**
     * Indicates if the radio options are to be displayed in a column instead of the regular row
     */
    radioInColumn: {
      type: Boolean,
      default: false
    },
    /**
     * Determines the type of files the user can send
     */
     fileTypes: {
      type: Array,
      required: false
    },
    /**
     * Determines if the user can send more than one file
     */
    multipleFiles: {
      type: Boolean,
      default: false
    },

    inputFieldContainerStyling: {
      type: Object,
    },

    inputContainerAlign: {
      type: String,
      default: 'flex-start'
    },

    selectOptionsList: {
      type: Array,
    },

    checkboxIconStyle: {
      type: Object,
    },

    indeterminate: {
      type: Boolean,
    },

    checkboxLabelStyle: {
      type: Object,
    },

    disableBranchNodes: {
      type: Boolean,
      default: false,
    },

    treeSelectPlaceHolder: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      options: null,
      isValid: true,
    }
  },

  computed: {
    /**
     * Defines the width of the input field.
     * @returns {number} the percentage value of the tab width used for the field.
     */
    inputFieldSize() {
      if (!isNaN(this.size)) {
        return parseInt(this.size);
      } else {
        switch (this.size) {
          case "xSmall":
            return 100 / 6;
          case "small":
            return 100 / 3;
          case "medium":
            return (100 / 3) * 2;
          case "large":
            return 100;
          default:
            return 100 / 3;
        }
      }
    },

    /**
     * If either the field, tab or form are disabled then the input will be disabled
     */
    disabled() {
      return this.inputDisabled || this.tabDisabled || this.formDisabled;
    },

    /**
     * Indicates if the field is a label
     */
    fieldIsLabel(){
      if(this.variableName){
        return this.variableName.includes("_label")
      }
    },

    /**
     * The placeholder for the select input
     */
    selectPlaceholder() {
      let placeholder = this.$i18n.t("SelectOption");
      if (this.disabled) {
        return "";
      } else if (this.placeholder !== null) {
        placeholder = this.placeholder;
      }
      return placeholder;
    },

    /**
     * Minimum value for a positive number input
     */
    minValue(){
      return this.inputType.includes("_pos") ? 0 : this.min;
    },

    fieldId() {
      if (this.variableName) {
        return this.variableName.replace(".", "");
      } else {
        return null;
      }
    },

    autoNumberFieldStep() {
      if (this.inputType === "float" || this.inputType === 'float_pos' || this.inputType === 'number_pos') {
        return 0.00001;
      }
      return 1;
    },

    valueType() {
      return typeof this.value;
    },

    getDate() {
      if (this.value) {
        let date = new Date(this.value);
        let month = date.toISOString().substring(0, 10);
        return month;
      } else return null;
    },

    dateTimeValue() {
      if (this.value) {
        let date = new Date(this.value);
        date.setHours(date.getHours() + 1);
        let dateTime = date.toISOString().substring(0, 16);
        return dateTime.replace(" ", "T");
      } else return null;
    }
  },

  methods: {
    ...mapActions(["GET_LIST", "fetchAllUsers"]),

    updateValue(e) {
      const value = e;
      this.$emit("input", e);
      if (this.label === 'Copy From') {
        bus.$emit("copyFromInputBus", e);
      }

      // Determine validation type from the label or validation object
      if (this.validationObject && this.validationObject.validation) {
        switch (this.validationObject.validation) {
          case 'email':
            this.isValid = this.isValidEmail(value);
            break;
          case 'password':
            this.isValid = this.isValidPassword(value);
            break;
          case 'confirm_password':
            const password_value = this.validationObject.password_value;
            this.isValid = this.isValidConfirmPassword(value, password_value);
            break;
          case 'date':
            this.isValid = this.isValidDate(value);
            break;
          case 'phone':
            this.isValid = this.isValidPhoneNumber(value);
            break;
          case 'length':
            const { min, max } = this.validationObject;
            this.isValid = this.isValidLength(value, min, max);
            break;
          case 'imei':
            this.isValid = this.isValidIMEI(value);
            break;
        }
      }
    },

    // Validation functions
    isValidEmail(email) { return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)},
    isValidPassword(password) {
      const minLength = 8
        return password.length >= minLength;
      },
    isValidConfirmPassword(confirm_password_value, password_value){
      return confirm_password_value === password_value;
    },
    isValidDate(date) {  return /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/.test(date)},
    isValidPhoneNumber(phone) { return /^\(\d{3}\) \d{3}-\d{4}$/.test(phone)},
    isValidLength(input, min, max) { return input.length >= min && input.length <= max},
    isValidIMEI(imei) { 
      const validIMEI = !utils.validateIMEI(imei)
      return !validIMEI
    },


    /**
     * Checks if the pressed key is a number key
     *
     * @param {Event} evt - the click event
     */
    isNumberKey(evt) {
      if (this.min === 0) {
        var charCode = evt.which ? evt.which : event.keyCode;
        return !(charCode > 31 && (charCode < 48 || charCode > 57));
      }
    },

    updateCheckboxValue(e) {
      this.$emit("updateCheckboxValue", e);
    },

    checkAlphanumeric(e) {
      this.$emit("check-alphanumeric", e);
    },

    /**
     * Checks if the key pressed is Alphanumeric and prevents the event from proceding if it's not
     * @param {Event} e - the key click event
     */
    nameKeydown(e) {
      if (!e.key.match(/^[a-zA-Z0-9]*$/)) {
        e.preventDefault();
      }
    },

    isFloat(n) {
      if (this.inputType === "float") {
        return true;
      } else if (this.inputType === "int") {
        return false;
      } else {
        return Number(n) === n && n % 1 !== 0;
      }
    },

    checkInput(e) {
      switch (this.inputType) {
        case "int_pos":
          this.checkIntegerPositive(e);
          break;
        case "int":
          this.checkIfMinusIsValid(e);
          this.checkInteger(e);
          break;
        case "number":
        case "float":
          this.checkIfMinusIsValid(e);
          this.checkFloat(e);
          break;
        case "number_pos":
        case "float_pos":
          this.checkFloatPositive(e);
          break;
        default:
          break;
      }
    },

    //only allow positive numbers
    checkFloat(e) {
      const regex = /^-?[\.,0-9]*$/;
      //if input is not a number a comma or a dot, validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    checkFloatPositive(e) {
      const regex = /^[\.,0-9]*$/;
      //if input is not a number a comma or a dot, validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    //only allow positive integers
    checkIntegerPositive(e) {
      const regex = /^[0-9]*$/;
      //if input is not a number validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    checkInteger(e) {
      const regex = /^-?[0-9]*$/;
      //if input is not a number validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    checkIfMinusIsValid(e){
    let value = this.value ? String(this.value) : "";
    let newkey= String(e.key);
    if(newkey.includes('-')){
        if(value.length !=0){
          e.preventDefault();
        }
        if(value.includes("-")){
        e.preventDefault();
      }
      }
    },

    /**
     * Loads the select options locally.
     */
     async loadOptions({ callback }) {
      let options = this.$store.getters.getListOfOptionsByListName(
        this.selectOptions,
        [this.value]
      );
      if(this.selectOptions != null){
      if (!!options && options.custom) {
        this.options = Object.values(options);
        this.options.pop();
        return this.options;
      } else if (!options) {
        try {
          options = await this.GET_LIST({
            listName: this.selectOptions,
            code: [this.value]
          });
        } catch (error) {
          console.log(error);
        }
      }
      }
      this.options = options;
      callback();
    },

    /**
     * Normalizes the content of each option object in the object list so it can be used in the Select input field.
     * @param {object} node - option object
     * @returns {{id: string | number, label: string}} a normalized option object.
     */
    normalizer(node) {
      let id = node.id ? node.id : "";
      let label = node.label ? node.label : "";
      switch (true) {
        case node.hasOwnProperty("code") || node.hasOwnProperty("id"):
          if (node.hasOwnProperty("id")) {
            id = node.id
          }
          if (node.hasOwnProperty("code")) {
            id = node.code;
          }
          if (node.hasOwnProperty("client_label") && node.client_label) {
            label = node.client_label;
          } else if (node.hasOwnProperty("libelle") && node.libelle) {
            label = node.libelle;
          } else if (node.hasOwnProperty("reference") && node.reference) {
            //cableMaterialList
            label = node.reference;
          } else if (node.hasOwnProperty("name") && node.name) {
            //cableMaterialList
            label = node.name;
          } else if (
            node.hasOwnProperty("municipality_label") &&
            node.municipality_label
          ) {
            label = node.municipality_label;
          } else if (node.hasOwnProperty("libelle") && !node.libelle) {
            label = node.code;
          }
          break;
        case node.hasOwnProperty("or_id"): //OwnerList
          id = node.or_id;
          label = node.or_nom;
          break;
        case node.hasOwnProperty("pw_id"): //PathwayIdsList
          id = node.pw_id;
          label = node.pw_id;
          break;
      }
      let disabled = node.deleted || node.disabled
      return {
        id: id,
        label: label,
        isDisabled: disabled
      };
    },

    loadUserOptions({ callback }) {
      this.fetchAllUsers()
        .then(resp => {
          this.options = resp.data.data;
        })
        .catch(error => {
          console.error(error);
        })
        .then(() => {
          callback();
        });
    },

    userNormalizer(node) {
      let userName = node.first_name;
      if (node.last_name) {
        userName += " " + node.last_name;
      }
      return { id: node.id, label: userName };
    },

    convertedValue(value) {
      let convertedValue = value;
      if (this.valueType === "number") {
        convertedValue = parseInt(value);
      } else if (this.valueType === "boolean") {
        convertedValue = value == "true";
      }
      return convertedValue;
    },

    getValidURL(url) {
      if (url.includes("https://")) {
        return url;
      } else {
        return "https://" + url;
      }
    },

    setDateTimeValue(value) {
      if (value) {
        value = value.replace("T", " ");
      }
      this.updateValue(value);
    },
  }
}

</script>

<style scoped>
.input-field {
  display: flex;
  flex-direction: column;
  padding: 4px 20px;
}

.input-field > label {
  margin-left: 0px;
  color: #767676;
  font-size: 10px;
  font-weight: 400;
}

.input-field-container {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.input-field .input-container {
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.input-field .input-container.required-fail {
  width: 100%;
  border-bottom: 1px solid var(--red);
}

.input-field .input-container .radio-input >>> .v-icon.v-icon {
  color: #29BDF0;
}

.input-field .input-container > input,
.input-field .input-container > a,
.input-field .input-container > textarea,
.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control,
.input-field .input-container > .url,
.input-field .input-container > .date > input,
.input-field .input-container > .datetime > input {
  width: 100%;
  height: 26px;
  padding: 4px 10px 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  color: rgba(118, 118, 118, 1) !important;
  border: 1px solid rgba(219, 219, 224, 1);
}

.input-field .input-container > input.invalid,
.input-field .input-container > textarea.invalid {
  border-bottom: 1px solid var(--red);
}

.input-field .input-container > input:focus,
.input-field .input-container > textarea:focus {
  border: 1px solid #DBDBE0;
}


.input-field .input-container > input.invalid:focus,
.input-field .input-container > textarea.invalid:focus {
  border-bottom: 1px solid var(--red);
}

.input-field .input-container > textarea {
  height: 60px;
}

.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__placeholder,
.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__single-value {
  line-height: 17px;
  color: rgba(118, 118, 118, 1) !important;
  padding: 0;
}

.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__multi-value .vue-treeselect__multi-value-item-container {
  padding-top: 1px;
  max-height: 15px;
}

.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__multi-value .vue-treeselect__multi-value-item {
  background: transparent;
  color: #767676;
  font-size: 10px;
  border: none;
  text-decoration: underline;
}

.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__multi-value .vue-treeselect__input-container,
.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__multi-value .vue-treeselect__input-container .vue-treeselect__input {
  padding: 0;
}

.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__multi-value .vue-treeselect__input-container {
  font-size: 10px;
}

.input-field > .input-field-container > .input-container > .form-input-select >>> .vue-treeselect__control .vue-treeselect__value-container::-webkit-scrollbar {
  display: none;
}

.input-field >>> .input-field-container > .input-container #radio-buttons {
  gap: 1px;
}

.input-field >>> .input-field-container > .input-container #radio-buttons label {
  font-size: 10px;
}

.input-field >>> .input-field-container > .input-container #radio-buttons .v-input--selection-controls__input {
  margin-right: 0px;
}

.form-input-clear-button{
  margin-top: 0 !important;
  width: 20px !important;
  height: 20px;
  position: absolute;
  right: 22px;
  top: 3px
}

.form-input-clear-button:hover{
  position: absolute;
}

.date,
.datetime {
  position: relative;
  width: 100%;
}

.disabled {
  color: #76767680 !important;
}

input:disabled {
  background: transparent;
}
</style>


// WEBPACK FOOTER //
// src/newComponents/_Atoms/InputField.vue